/**
 * Sözleşme DETAYININ bölümleri.
 *
 * ### Neden bir kayıt dosyası
 *
 * `contractSections.ts` ve `organizationSections.ts` ile AYNI kural: sıra sabittir, bölüm
 * listesi tek yerde durur, kabuk "hangi sayfalar var" sorusunu bilmez. Üçüncü kez aynı
 * deseni yazmak yerine aynı deseni KOPYALAMAK, birinin gün gelip diğerinden ayrışmasını
 * engelliyor.
 *
 * ### ⚠ Bölümler AYRI ROTALAR, sekme state'i DEĞİL
 *
 * Detay ekranı bu kuralın tek istisnasıydı: `useState<BolumSekmesi>` tutuyordu ve dört
 * bölümün tamamı `hidden` sınıfıyla aynı anda DOM'da duruyordu. Ölçülen üç sonucu vardı —
 * bölüme bağlantı verilemiyor, geri tuşu sekmeyi geri almıyor, yenilemede kullanıcı ilk
 * sekmeye düşüyordu. Dördüncüsü görünmezdi: dört bölümün bütün sorguları her açılışta
 * koşuyordu.
 *
 * Kardeş kabukların ikisi de bunu zaten yazmıştı (`ContractsLayout`: *"kullanıcı ... bir
 * bağlantı olarak paylaşabilmeli ve yenilemede bağlam kaybolmamalı"*). Burada yeni bir
 * karar alınmıyor, var olan karar detay ekranına da uygulanıyor.
 *
 * ### ⚠ Yetki kapısı YOK — bilerek
 *
 * Kardeş kayıtlarda her bölümün bir `permission` alanı var, çünkü onlar birbirinden
 * bağımsız YÜZEYLER. Detay bölümleri ise TEK BİR KAYDIN yüzleri: kapı kaydın kendisinde
 * (`requireRecordAccess`) ve kabuk o kapıyı bir kez geçiyor. Bölüm başına ikinci bir kapı
 * koymak, aynı erişim kararını iki yerde hesaplamak olurdu — bölümlerin içindeki eylemler
 * (düzenle, davet et, süreç başlat) kendi kapılarını zaten ayrıca uyguluyor.
 */

export interface ContractDetailSection {
  key: string;
  label: string;
  /**
   * `/app/sozlesmeler/:id` altındaki GÖRELİ yol. Özet için boş dize — o, index rota.
   *
   * Kardeş kayıtlar mutlak `route` tutuyor; burada tutulamaz çünkü yol `:id` içeriyor.
   * Mutlak yolu `contractDetailSectionPath()` üretiyor.
   */
  path: string;
}

/** ⚠ SIRA KANONİKTİR: durum → çalışma → ilerletme → kim → arşiv. */
export const CONTRACT_DETAIL_SECTIONS: readonly ContractDetailSection[] = [
  /** "Nerede duruyor, benden ne bekleniyor" — kaydı açan herkesin ilk sorusu. */
  { key: "ozet", label: "Özet", path: "" },
  /**
   * BELGE — ürünün çalışma yüzeyi.
   *
   * ⚠ Metin bugüne kadar sekme DEĞİL, başlığın altındaki bir DÜĞMEYDİ ("Sözleşme metnini
   * aç") ve versiyon/revizyon/yorum ondan da ayrı üç yerdeydi. Bir maddeye gelen revizyon
   * önerisini değerlendirmek için kullanıcı üç yüzey arasında gidip geliyordu; oysa bu
   * TEK bir iş.
   */
  { key: "belge", label: "Belge", path: "belge" },
  /** Süreçler: inceleme · iç onay · taraf mutabakatı · imza. */
  { key: "surec", label: "Süreç", path: "surec" },
  /**
   * ⚠ TARAF ve ERİŞİM TEK BÖLÜMDE: ikisi de "kim dahil, ne görebiliyor" sorusunu
   * cevaplıyor ve üründe bugün de yan yana duruyorlar. Ayırmak, taraf eklerken erişimi
   * başka sekmede yönetmek demekti — oysa kullanıcının en sık karıştırdığı şey tam olarak
   * bu ikisinin farkı (taraf olmak erişim VERMEZ) ve fark ancak yan yana anlatılabiliyor.
   */
  { key: "taraflar", label: "Taraflar ve Erişim", path: "taraflar" },
  /** İdari yüz: müvekkil, bağlı kayıtlar, yükümlülükler, ekler, notlar, geçmiş, dışa aktarma. */
  { key: "kayit", label: "Kayıt", path: "kayit" },
];

/** Bölümün mutlak adresi. Geri dönüş bağlamı (`?liste=`) çağıranın sorumluluğunda. */
export function contractDetailSectionPath(contractId: string, path: string): string {
  return path === "" ? `/app/sozlesmeler/${contractId}` : `/app/sozlesmeler/${contractId}/${path}`;
}

/**
 * Adresteki bölümün anahtarı; eşleşme yoksa `null`.
 *
 * ⚠ Özet (boş yol) EN SONA bakılıyor: `""` her yolun ön eki olduğu için önce bakılsaydı
 * bütün bölümlerde "Özet" aktif görünürdü.
 */
export function activeContractDetailSection(
  pathname: string,
  contractId: string,
  sections: readonly ContractDetailSection[] = CONTRACT_DETAIL_SECTIONS,
): string | null {
  const kok = `/app/sozlesmeler/${contractId}`;
  const eslesen = sections
    .filter((s) => s.path !== "")
    .find((s) => pathname === `${kok}/${s.path}` || pathname.startsWith(`${kok}/${s.path}/`));
  if (eslesen) return eslesen.key;
  return pathname === kok ? "ozet" : null;
}
